import { canUseRichText } from './userRoleHierarchy';

/**
 * Whether the user has opted into rich text (defaults to true for permitted roles).
 * Reads `richTextEnabled` first, then legacy `enableRichTextEditor`.
 * @param {{ role?: string, richTextEnabled?: boolean, enableRichTextEditor?: boolean } | null | undefined} userData
 * @returns {boolean}
 */
export function resolveRichTextEnabled(userData) {
  if (!canUseRichText(userData?.role)) return false;
  const pref = userData?.richTextEnabled ?? userData?.enableRichTextEditor;
  if (pref === false) return false;
  return true;
}

/**
 * @param {object | null | undefined} userData
 * @returns {object | null | undefined}
 */
export function enrichUserDataWithRichText(userData) {
  if (!userData || typeof userData !== 'object') return userData;
  return {
    ...userData,
    richTextEnabled: resolveRichTextEnabled(userData),
  };
}

/**
 * @param {{ role?: string, richTextEnabled?: boolean, enableRichTextEditor?: boolean } | null | undefined} userData
 * @returns {boolean}
 */
export function canUseTicketRichText(userData) {
  return resolveRichTextEnabled(userData);
}

/**
 * @param {string | null | undefined} body
 * @param {string | null | undefined} bodyFormat
 * @returns {boolean}
 */
export function isHtmlBody(body, bodyFormat) {
  if (bodyFormat === 'html') return true;
  if (typeof body !== 'string') return false;
  return /<[a-z][\s\S]*>/i.test(body);
}

/**
 * @param {string | null | undefined} html
 * @returns {string}
 */
export function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  if (typeof document !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').trim();
  }
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * @param {string | null | undefined} html
 * @returns {boolean}
 */
export function isRichTextEmpty(html) {
  if (!html || !html.trim()) return true;
  return !stripHtml(html);
}

/**
 * @param {string} text
 * @returns {string}
 */
export function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {string} text
 * @returns {string}
 */
export function plainTextToHtml(text) {
  if (!text || !text.trim()) return '<p></p>';
  return text
    .split('\n')
    .map((line) => `<p>${escapeHtml(line) || '<br>'}</p>`)
    .join('');
}

/**
 * @param {string} html
 * @returns {string}
 */
export function normalizeRichTextHtml(html) {
  if (!html || isRichTextEmpty(html)) return '';
  return html.trim();
}

export function buildTicketBodyPayload(formBody, userData) {
  if (canUseTicketRichText(userData)) {
    return {
      body: normalizeRichTextHtml(formBody),
      bodyFormat: 'html',
    };
  }
  return {
    body: typeof formBody === 'string' ? formBody.trim() : '',
    bodyFormat: 'plain',
  };
}

/**
 * Preview text for logs and table cells.
 * @param {string | null | undefined} body
 * @param {string | null | undefined} bodyFormat
 * @param {number} [maxLen]
 * @returns {string}
 */
export function bodyPreviewText(body, bodyFormat, maxLen = 120) {
  const raw = isHtmlBody(body, bodyFormat) ? stripHtml(body) : (body || '');
  const trimmed = raw.trim();
  if (!maxLen || trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen)}…`;
}
